/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, getSimpleProducts, ProductInput } from '@msdyn365-commerce-modules/retail-actions';

import { ProductComparisonItem, ProductComparisonState } from '../../product-comparison-state';

/**
 * Represents the data for a single product in the comparison.
 */
export interface IProductComparisonProductData {
    itemId: string;
    product: SimpleProduct;
    catalogId: number;
}

/**
 * Input class for GetProductsInProductComparisonInput data action.
 */
export class GetProductsInPreviewProductComparisonInput implements IActionInput {
    public readonly items: ProductComparisonItem[];

    public constructor(items: ProductComparisonItem[]) {
        this.items = items;
    }

    /**
     * Cache key.
     * @returns Cache key.
     */
    public getCacheKey = (): string => 'GetProductsInPreviewProductComparisonInput';

    /**
     * Cache object type.
     * @returns Cache object type.
     */
    public getCacheObjectType = (): string => 'GetProductsInPreviewProductComparisonInput';

    /**
     * Data cache type.
     * @returns Data cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Creates input for the data action.
 * @param inputData - Action context info.
 * @returns Action input.
 */
export const createGetProductsInPreviewProductComparisonInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): GetProductsInPreviewProductComparisonInput => {
    const items = ProductComparisonState.instance(inputData.requestContext).items;
    return new GetProductsInPreviewProductComparisonInput(items);
};

/**
 * Retrieves product data for the items in the comparison.
 * @param input - Action input.
 * @param actionContext - Action context.
 * @returns Products data.
 */
export const getProductsInPreviewProductComparisonAction = async (
    input: GetProductsInPreviewProductComparisonInput,
    actionContext: IActionContext
): Promise<IProductComparisonProductData[]> => {
    const catalogId = getCatalogId(actionContext.requestContext);

    const newInput = input.items.filter(item => item.catalogId === catalogId);
    if (!ArrayExtensions.hasElements(newInput)) {
        return [];
    }

    const productInputs = newInput.map(
        item => new ProductInput(item.productId, actionContext.requestContext.apiSettings, undefined, undefined, undefined, item.catalogId)
    );

    const simpleProducts = await getSimpleProducts(productInputs, actionContext);
    if (newInput.length !== simpleProducts.length) {
        throw new Error('Product comparison preview: Failed to retrieve products info.');
    }

    return newInput.map((item, index) => {
        return {
            itemId: ProductComparisonItem.getItemId(item.productId, item.catalogId),
            product: simpleProducts[index],
            catalogId: item.catalogId
        };
    });
};

/**
 * Data action to retrieve products info for the items in the comparison.
 */
export const getProductsInPreviewProductComparisonDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/product-comparison/product-comparison-preview/get-products-in-preview-product-comparison',
    action: getProductsInPreviewProductComparisonAction as IAction<IProductComparisonProductData[]>,
    input: createGetProductsInPreviewProductComparisonInput
});

export default getProductsInPreviewProductComparisonDataAction;
